import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Emitido por `assets/purchase-options.js` cuando cambia el pack elegido.
 *
 * DUPLICADO a proposito (alla esta la constante `PACK_TIER_CHANGE`, con la explicacion larga):
 * importarlo invertiria la dependencia — este modulo carga en TODA ficha de producto y aquel
 * solo en las que tienen packs, asi que el import arrastraria `purchase-options.js` y
 * `pack-tiers.js` a paginas que no los usan. Si se cambia el nombre, cambiarlo en los dos lados.
 */
const PACK_TIER_CHANGE = 'pack-tier:change';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(PACK_TIER_CHANGE, this.#handlePackTierChange, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  /**
   * Mantiene la barra flotante mostrando el pack que esta elegido abajo.
   *
   * Antes esta barra renderizaba el precio de la variante base y no se movia nunca: decia
   * $89.000 mientras el cliente tenia marcado el pack de 2, que cuesta $160.200 (medido en vivo).
   * Como la barra viaja con el scroll, era el numero equivocado mas visible de la pagina.
   *
   * Los dos `ref` son opcionales a proposito y no van en `requiredRefs`: solo existen en modo
   * pack, y la clase base LANZA en `connectedCallback` si falta un ref requerido — un producto
   * sin packs se quedaria con la barra montada pero muerta, sin boton de comprar.
   *
   * @param {Event} event
   */
  #handlePackTierChange = (event) => {
    if (!(event instanceof CustomEvent)) return;
    const { productId, label, totalFormatted } = event.detail ?? {};

    // Guarda para el caso de dos productos con packs en la misma pagina (una seccion de
    // producto destacado junto a la ficha, por ejemplo): esta barra solo responde al suyo.
    if (productId && this.dataset.productId && String(productId) !== String(this.dataset.productId)) return;

    const { packPrice, packLabel } = this.refs;
    if (packPrice && totalFormatted) packPrice.textContent = totalFormatted;
    if (packLabel && label) packLabel.textContent = label;
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    // En un producto con packs no existe el bloque de compra nativo: blocks/buy-buttons.liquid se
    // apaga solo cuando el producto tiene `custom.pack_tiers`, y la compra vive en el widget de
    // blocks/purchase-options.liquid. Sin este fallback la funcion salia por la primera guarda y
    // la barra quedaba en el DOM sin observador, o sea invisible para siempre.
    const anchor = productForm?.closest('.buy-buttons-block') ?? this.#getPurchaseOptions();
    if (!anchor) return;

    // Deliberadamente NO se observa el <footer>. Horizon traia un segundo IntersectionObserver
    // sobre el, que escondia la barra apenas el footer entraba en el viewport (con
    // `rootMargin: '200px 0px 0px 0px'`, o sea 200px antes). Su unica rama de reaparicion exigia
    // que el footer volviera a SALIR del viewport, cosa que solo pasa subiendo: bajando hasta el
    // final de la pagina la barra desaparecia y ya no volvia. Ademas el <footer> de este tema
    // envuelve las cuatro secciones del footer-group (layout/theme.liquid), asi que es altisimo y
    // el disparo ocurria mucho antes de llegar al fondo.
    //
    // Queda un unico observer, el de los buy buttons, que ya expresa la regla que queremos:
    // visible mientras el bloque de compra este por encima del viewport, oculta al volver a el.
    // La compensacion para que el footer no quede tapado en movil es CSS, en
    // sections/product-information.liquid.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(anchor);
    this.#targetAddToCartButton = productForm?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * El widget de packs de esta seccion, si el producto tiene tiers cargados.
   * Es a la vez el ancla del observador y el destino del boton en productos con packs.
   */
  #getPurchaseOptions() {
    return this.closest('.shopify-section')?.querySelector('purchase-options-component') ?? null;
  }

  /**
   * El primer ancestro que realmente scrollea, o `null` si el que scrollea es el documento.
   * No basta con que `overflow-y` sea desplazable: hay contenedores declarados `auto` que no
   * desbordan y no se mueven. Se exige tambien desborde real.
   *
   * @param {Element} el
   * @returns {HTMLElement | null}
   */
  #getScrollingAncestor(el) {
    for (let node = el.parentElement; node instanceof HTMLElement; node = node.parentElement) {
      const { overflowY } = getComputedStyle(node);
      const desplazable = overflowY === 'auto' || overflowY === 'scroll';

      if (desplazable && node.scrollHeight > node.clientHeight + 1) return node;
    }

    return null;
  }

  /**
   * Alto que el encabezado le roba a la parte de arriba de la vista.
   *
   * Solo cuenta cuando flota sobre el contenido, y para saberlo hay que subir por los ancestros:
   * medido en vivo, `#header-component` computa `position: static` en las dos pantallas — el
   * `sticky` vive en el <header> de seccion que lo envuelve. Mirar solo el elemento devolvia
   * siempre 0 y el widget podia quedar tapado.
   *
   * @returns {number}
   */
  #getHeaderInset() {
    const header = document.querySelector('#header-component');
    if (!(header instanceof HTMLElement)) return 0;

    let flotante = false;
    for (let node = header; node instanceof HTMLElement; node = node.parentElement) {
      const { position } = getComputedStyle(node);
      if (position === 'fixed' || position === 'sticky') {
        flotante = true;
        break;
      }
    }
    if (!flotante) return 0;

    // Ya pegado arriba (top ~ 0) el estorbo es su borde inferior. Todavia sin pegar (arranque de
    // la pagina, top > 0) el estorbo es su ALTO, que es lo que va a ocupar en cuanto se pegue:
    // usar el borde inferior ahi reservaria de mas el aire que hay por encima del encabezado.
    const { top, bottom } = header.getBoundingClientRect();

    return Math.max(0, bottom - Math.max(0, top));
  }

  // Public action handlers

  /**
   * Boton de la barra en productos con packs: lleva al selector en vez de agregar al carrito.
   * El porque esta explicado en el markup, en sections/product-information.liquid.
   */
  handleScrollToOptions = (event) => {
    event?.preventDefault();

    const options = this.#getPurchaseOptions();
    if (!options) return;

    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    // El contenedor que scrollea se BUSCA, no se asume.
    //
    // La version anterior hacia `this.closest('.page-wrapper')` y, si encontraba el elemento,
    // le pedia `scrollTo()`. En escritorio funciona porque ahi `.page-wrapper` tiene
    // `overflow-y: auto`. En movil NO: medido en vivo a 390px, `.page-wrapper` computa
    // `overflow-y: visible` y el que scrollea es el documento. Como `closest()` igual devolvia el
    // elemento, entraba por esa rama, le pedia scroll a algo que no scrollea, y el `else` con
    // `scrollIntoView` no llegaba a correr nunca: el boton no hacia absolutamente nada.
    //
    // Buscar el primer ancestro que de verdad desborde y este declarado desplazable cubre los dos
    // casos sin hardcodear ninguna clase, y sigue siendo determinista (no depende de que
    // `scrollIntoView` resuelva bien el ancestro, que es lo que ya habia fallado en el preview).
    const scroller = this.#getScrollingAncestor(options);
    const rect = options.getBoundingClientRect();

    // Se centra el widget en el espacio util, descontando el encabezado cuando este flotando
    // sobre el contenido. Si el widget es mas alto que ese espacio, se alinea arriba.
    const inset = this.#getHeaderInset();
    const alturaVisible = scroller ? scroller.clientHeight : window.innerHeight;
    const libre = alturaVisible - inset - rect.height;
    const offset = libre > 0 ? inset + libre / 2 : inset;

    if (scroller) {
      const relativo = rect.top - scroller.getBoundingClientRect().top;
      const top = Math.max(
        0,
        Math.min(scroller.scrollTop + relativo - offset, scroller.scrollHeight - scroller.clientHeight)
      );

      scroller.scrollTo({ top, behavior });
    } else {
      window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - offset), behavior });
    }

    // Mover el foco al tier elegido para que el teclado y el lector de pantalla queden donde
    // quedo la vista; sin `preventScroll` el navegador haria un segundo salto que pelea con el
    // desplazamiento de arriba.
    const tier =
      options.querySelector('input[type="radio"]:checked') ?? options.querySelector('input[type="radio"]');
    if (tier instanceof HTMLElement) tier.focus({ preventScroll: true });
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;


    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
